import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { AuthorizedUser } from '@/types/authorizedUser';

interface ImportUsersDialogProps {
  onImportSuccess: (users: AuthorizedUser[]) => void;
}

/**
 * Parses a simple CSV (comma-separated, first row is header) into
 * AuthorizedUser records. Expected headers: name, email, accountNumber,
 * licenseNumber. Extra columns are ignored.
 */
const parseCsv = (text: string): AuthorizedUser[] => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const idx = (name: string) => headers.indexOf(name.toLowerCase());

  const nameIdx = idx('name');
  const emailIdx = idx('email');
  const accountIdx = idx('accountnumber');
  const licenseIdx = idx('licensenumber');

  if (nameIdx < 0 || emailIdx < 0) {
    throw new Error('CSV must include at least "name" and "email" columns');
  }

  return lines.slice(1).map((line, i) => {
    const cols = line.split(',').map((c) => c.trim());
    return {
      id: `import-${Date.now()}-${i}`,
      name: cols[nameIdx] ?? '',
      email: cols[emailIdx] ?? '',
      accountNumber: accountIdx >= 0 ? cols[accountIdx] ?? '' : '',
      licenseNumber: licenseIdx >= 0 ? cols[licenseIdx] ?? '' : '',
    };
  });
};

const ImportUsersDialog: React.FC<ImportUsersDialogProps> = ({ onImportSuccess }) => {
  const { toast } = useToast();
  const [isImporting, setIsImporting] = useState(false);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const handleImport = async () => {
    if (!file) return;
    setIsImporting(true);
    try {
      const text = await file.text();
      const users = parseCsv(text);
      if (users.length === 0) {
        throw new Error('No rows found in CSV');
      }
      onImportSuccess(users);
      toast({
        title: 'Import Successful',
        description: `Imported ${users.length} users from CSV`,
      });
      setOpen(false);
      setFile(null);
    } catch (error) {
      toast({
        title: 'Import Failed',
        description: error instanceof Error ? error.message : 'Failed to parse CSV',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-10">
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Import from CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Upload className="mr-2 h-5 w-5" />
            Import Authorized Users
          </DialogTitle>
          <DialogDescription>
            Upload a CSV with columns: <code>name, email, accountNumber, licenseNumber</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <Label htmlFor="csv-file">CSV file</Label>
          <Input
            id="csv-file"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!file || isImporting}>
            {isImporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Import Users
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportUsersDialog;
